import { HttpBase, type HttpResult } from "@library/Security";
import type { IDealerModeVehicle, IDealerModeVehicleDto } from "../IDealerModeVehicle";
import type { IDealerModeAppConfigurationDto } from "@domain/AppConfiguration/DealerModeAppConfiguration";

export const useDealerModeApi = () => new class extends HttpBase
{

    /**
     * Searches for a vehicle within a set of apps, returning claimed and unclaimed vehicles
     * @param query
     * @param appConfigurationIds
     * @returns
     */
    async searchAll( query: string, appConfigurationIds: string[] ): Promise<IDealerModeVehicleDto[]>
    {
        const result = await this.get( `api/dealermode/search`, { query, appConfigurationIds, includeClaimed: true } );
        if( result.success ) return result.data.items;
        return [];
    }

    /**
     * Searches for a vehicle within a set of apps, only returning unclaimed vehicles
     * @param query
     * @param appConfigurationIds
     * @returns
     */
    async findVehicleForActivation( rego: string, appConfigurationIds: string[] ): Promise<IDealerModeVehicleDto|null>
    {
        const result = await this.post( `api/dealermode/find-vehicle-for-activation`, { rego, appConfigurationIds } );
        if( result.success ) return result.data.vehicle;
        return null;
    }

    /**
     * Creates a vehicle from the provided model data
     */
    async createVehicle( model: IDealerModeVehicle ) : HttpResult<{ vehicle: IDealerModeVehicleDto }>
    {
        const result = await this.post( 'api/dealermode/create', model, true );
        return result;
    }

    /**
     * Activates an existing vehicle from the provided model data
     */
    async activateVehicle( model: IDealerModeVehicle ) : HttpResult<{ vehicle: IDealerModeVehicleDto }>
    {
        const result = await this.post( 'api/dealermode/activate', model, true );
        return result;
    }

    /**
     * Updates an existing vehicle from the provided model data
     */
    async updateVehicle( model: IDealerModeVehicle ) : HttpResult<{ vehicle: IDealerModeVehicleDto }>
    {
        const result = await this.put( 'api/dealermode/update', model, true );
        return result;
    }

    /**
     * Gets a vehicle using its VehicleId
     * @param vehicleId
     * @returns
     */
    async getVehicle( vehicleId: string ): HttpResult<{ vehicle: IDealerModeVehicleDto }>
    {
        const result = await this.get( `api/dealermode/vehicle`, { vehicleId } );
        return result;
    }

    /**
     * Sends the welcome to Carbucks SMS
     */
    async resendWelcomeSms( vehicle: IDealerModeVehicle, mobile: string ) : HttpResult<{wasSuccessful:boolean}>
    {
        const request = {
            appConfigurationId: vehicle.appConfigurationId,
            vehicleId: vehicle.vehicleId,
            mobile: mobile
        }
        const result = await this.post( 'api/dealermode/resend-welcome-message', request );
        return result;
    }

    /**
     * Retrieves a DealerModeAppConfigurationDto for each provided appConfigurationId
     */
    async listAppConfigurationsAsync( appConfigurationIds: string[] ): HttpResult<{ appConfigurations: IDealerModeAppConfigurationDto[] }>
    {
        const result = await this.post( 'api/dealermode/list/app-configurations', { appConfigurationIds } );
        return result;
    }
}
